import { useSelector } from 'react-redux';
import { ApplicationState, WithServiceId } from '@mews-commander/types';
import { getCurrentCurrencyCultureInfo } from '@mews-commander/core/legacy-globalization';
import { getEnterpriseDefaultCurrencyCode } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getExchangeRates } from '@mews-commander/legacy-entities/src/ducks/exchangeRates';
import { getEnterpriseIdFromServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { useFormValueSelector } from '@mews-commander/legacy-form';
import { AMOUNT, CURRENCY, PRODUCT_SERVICE_FORM_NAME, UNITS } from '../constants';
import { ServiceFormValues } from '../types';
import {
    getAllProductUnitsSum,
    getCurrencyArray,
    mapCostToDefaultCurrency,
    sanitizeProductUnitsValues,
} from '../utils';

export const useProductServiceOrderTotal = ({ serviceId }: WithServiceId) => {
    const exchangeRates = useSelector(getExchangeRates);
    const enterpriseId = useSelector((state: ApplicationState) => getEnterpriseIdFromServiceId(state, { serviceId }));
    const currentCurrencyCultureInfo = useSelector(getCurrentCurrencyCultureInfo);
    const defaultCurrency = useSelector((state: ApplicationState) =>
        getEnterpriseDefaultCurrencyCode(state, { enterpriseId })
    );

    const unitsFormState: NonNullable<ServiceFormValues[typeof UNITS]> = useFormValueSelector(
        PRODUCT_SERVICE_FORM_NAME,
        UNITS
    );
    const defaultProductCurrencyFormState: ServiceFormValues[typeof CURRENCY] = useFormValueSelector(
        PRODUCT_SERVICE_FORM_NAME,
        CURRENCY
    );
    const defaultProductCostFormState: ServiceFormValues[typeof AMOUNT] = useFormValueSelector(
        PRODUCT_SERVICE_FORM_NAME,
        AMOUNT
    );

    const unitsFormValue = Object.values(unitsFormState || {});

    if (defaultProductCurrencyFormState) {
        unitsFormValue.push({
            unitCost: defaultProductCostFormState,
            unitCurrency: defaultProductCurrencyFormState,
            unitAmount: '1',
        });
    }

    const validatedProductUnitsValues = sanitizeProductUnitsValues(unitsFormValue);
    const currencyArray = getCurrencyArray(validatedProductUnitsValues);
    const hasMultipleCurrencies = currencyArray.length > 1;

    const productUnits = hasMultipleCurrencies
        ? mapCostToDefaultCurrency(exchangeRates, currentCurrencyCultureInfo)(validatedProductUnitsValues)
        : validatedProductUnitsValues;

    return {
        value: getAllProductUnitsSum(currentCurrencyCultureInfo)(productUnits),
        currency: hasMultipleCurrencies ? defaultCurrency : currencyArray[0],
        isConvertedToDefaultCurrency: hasMultipleCurrencies,
    };
};
